import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

type AuthPageShellProps = {
  title: string
  subtitle?: string
  children: ReactNode
  footerNote?: ReactNode
}

/**
 * Full-page centered auth card (login / register / reset).
 * Renders inside the global Header + Footer shell from Routes.
 */
const AuthPageShell = ({
  title,
  subtitle,
  children,
  footerNote,
}: AuthPageShellProps) => {
  const { t } = useTranslation()

  return (
    <div className="flex min-h-[calc(100vh-8rem)] flex-col items-center justify-center px-4 py-10 sm:py-14">
      <div className="w-full max-w-[26rem]">
        <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-[0_12px_40px_rgba(15,23,42,0.08)]">
          <div className="border-b border-gray-100 px-6 pb-5 pt-7 sm:px-8">
            <h1 className="text-center text-2xl font-bold tracking-tight text-gray-900 sm:text-[1.65rem]">
              {title}
            </h1>
            {subtitle ? (
              <p className="mx-auto mt-2 max-w-sm text-center text-sm leading-relaxed text-gray-500">
                {subtitle}
              </p>
            ) : null}
          </div>
          <div className="px-6 py-6 sm:px-8 sm:py-7">{children}</div>
        </div>

        {footerNote !== undefined ? (
          footerNote
        ) : (
          <div className="mt-5 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-xs text-gray-500">
            <span className="inline-flex items-center gap-1.5">
              <svg
                className="h-3.5 w-3.5 text-imovPrimary"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
                aria-hidden
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                />
              </svg>
              {t('auth.trustSecure')}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="h-1 w-1 rounded-full bg-gray-400" aria-hidden />
              {t('auth.trustQuebec')}
            </span>
          </div>
        )}
      </div>
    </div>
  )
}

export default AuthPageShell
